import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { lazily } from "react-lazily";
import React, { Suspense, useEffect, useState } from "react";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { Layout } from "@/layouts/Layout";
import { adminRoutes } from "@/routes/admin";
import { businessRoutes } from "@/routes/business";
import { authRoutes } from "@/routes/auth";
import { shared_routes } from "@/routes/shared_routes";
import { normalRoutes } from "@/routes/normal";
import { authActions } from "./redux/reducers/auth";
import { publicRoutes } from "./routes/public";
import authService from "@services/auth";

const { ProtectedRoutes } = lazily(() => import("@components/ProtectedRoutes"));

const TaleCustomerSubscribe = React.lazy(() =>
    import("@pages/subscribe/index")
);

export default function App() {
  const {
    isAuthenticated: auth0IsAuthenticated,
    getAccessTokenSilently,
    user: auth0User,
    isLoading: auth0IsLoading,
  } = useAuth0();
  const dispatch = useDispatch();
  const [appLoading, setAppLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const authType = localStorage.getItem("auth_type");
        const accessToken = localStorage.getItem("access_token");

        // Check for local authentication first
        if (authType === "local" && accessToken) {
          console.log("Initializing with local authentication");

          // Get user from localStorage or fetch from backend
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            const user = JSON.parse(storedUser);
            console.log("Loaded user from localStorage:", user);
            dispatch(
                authActions.setUser({
                  ...user,
                  "tale/roles": user.roles || [user.userType],
                })
            );
          } else {
            // Fetch user from backend
            try {
              const userData = await authService.getCurrentUserLocal();
              if (userData) {
                console.log("Fetched user from backend:", userData);
                dispatch(
                    authActions.setUser({
                      ...userData,
                      "tale/roles": userData.roles || [userData.userType],
                    })
                );
                localStorage.setItem("user", JSON.stringify(userData));
              }
            } catch (error) {
              console.error("Failed to fetch local user:", error);
              // Token might be invalid, clear it
              authService.logout();
            }
          }
        }
        // Check for Auth0 authentication
        else if (auth0IsAuthenticated && !auth0IsLoading) {
          console.log("Initializing with Auth0 authentication");

          try {
            const token = await getAccessTokenSilently();
            localStorage.setItem("access_token", token);
            localStorage.setItem("auth_type", "auth0");

            // Sync user with backend
            const syncResult = await authService.syncUser();

            dispatch(
                authActions.setUser({
                  ...auth0User,
                  userId: syncResult?.userId,
                  "tale/roles": auth0User?.["tale/roles"] || [],
                })
            );
          } catch (err) {
            console.error("Failed to initialize Auth0:", err);
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setAppLoading(false);
      }
    };

    // Wait for Auth0 to finish loading before initializing
    if (!auth0IsLoading) {
      initializeAuth();
    }
  }, [
    dispatch,
    getAccessTokenSilently,
    auth0IsAuthenticated,
    auth0User,
    auth0IsLoading,
  ]);

  const routes = [
    // Auth routes (login, register, etc.) - no protection needed
    ...authRoutes,

    // Public routes (public surveys, etc.) - no protection needed
    ...publicRoutes,

    // Admin routes - ADMIN only
    {
      path: "/admin",
      caseSensitive: true,
      element: <Layout overrideRole="admin" />,
      children: [
        {
          element: <ProtectedRoutes roles={["ADMIN"]} />,
          children: adminRoutes,
        },
      ],
    },

    // Subscribe page - COMPANY_USER only
    {
      path: "/subscribe",
      caseSensitive: true,
      element: (
          <ProtectedRoutes roles={["COMPANY_USER"]}>
            <TaleCustomerSubscribe />
          </ProtectedRoutes>
      ),
    },

    // Main app routes with Layout
    {
      path: "/",
      caseSensitive: true,
      element: <Layout />,
      children: [
        // Business-specific routes (surveys creation, interviews, etc.) - COMPANY_USER only
        {
          element: <ProtectedRoutes roles={["COMPANY_USER"]} />,
          children: businessRoutes,
        },

        // Normal user routes (awards) - NORMAL_USER only
        {
          element: <ProtectedRoutes roles={["NORMAL_USER"]} />,
          children: normalRoutes,
        },

        // Shared routes (home, surveys list, focus groups, account, etc.)
        // Available to COMPANY_USER, NORMAL_USER, and ADMIN
        {
          element: (
              <ProtectedRoutes roles={["COMPANY_USER", "NORMAL_USER", "ADMIN"]} />
          ),
          children: shared_routes("COMPANY_USER"),
        },
      ],
    },
  ];

  const finalRoutes = createBrowserRouter(routes);

  // Show loading while Auth0 is loading OR while we're initializing local auth
  const isLoading = auth0IsLoading || appLoading;

  return isLoading ? (
      <TaleLoadingFallback />
  ) : (
      <Suspense fallback={<TaleLoadingFallback />}>
        <RouterProvider
            fallbackElement={
              <div className="w-full h-[100vh] flex items-center justify-center">
                <TaleLoadingFallback />
              </div>
            }
            router={finalRoutes}
        />
      </Suspense>
  );
}
